import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPblOcContext, getProBonoDirectoryLawyers, setPblDirectoryFavorite } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import type { PblOcContextOffice } from '@/types/pbl';
import { errorHandler } from '@/utils/toastHandler';
import { mapDirectoryEntryToOcLawyer } from './caseMatchingState';
import LawyerReviewSummaryModal from './LawyerReviewSummaryModal';
import type { PblOcLawyer } from './types';
import {
  DirectoryActions,
  DirectoryBadge,
  DirectoryButton,
  DirectoryDescription,
  DirectoryField,
  DirectoryIdentity,
  DirectoryInput,
  DirectoryList,
  DirectoryName,
  DirectoryPagination,
  DirectoryPanel,
  DirectoryRow,
  DirectoryState,
  DirectoryToolbar,
} from './lawyerDirectoryStyles';

const LawyerDirectory = () => {
  // @ts-ignore -- 既有翻譯目錄超過 CRA 增量編譯器的遞迴型別推斷上限。
  const { t, i18n } = useTranslation();
  const [offices, setOffices] = useState<PblOcContextOffice[]>([]);
  const [officeId, setOfficeId] = useState(0);
  const [lawyers, setLawyers] = useState<PblOcLawyer[]>([]);
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [preview, setPreview] = useState<PblOcLawyer | null>(null);

  useEffect(() => {
    let active = true;
    getPblOcContext()
      .then(({ data }) => {
        if (!active) return;
        if (data.success !== true || !data.data.offices.length) throw new Error('No OC office');
        setOffices(data.data.offices);
        setOfficeId(
          (current) => current || data.data.default_office?.pbl_oc_office_id || data.data.offices[0].pbl_oc_office_id
        );
      })
      .catch(() => {
        if (active) {
          setFailed(true);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refresh]);

  useEffect(() => {
    if (!officeId) return;
    let active = true;
    setLoading(true);
    setFailed(false);
    setLawyers([]);
    getProBonoDirectoryLawyers({ office_id: officeId, keyword: query, page, per_page: 20 })
      .then(({ data }) => {
        if (!active) return;
        if (data.success !== true) throw new Error('Directory unavailable');
        setLawyers(data.data.data.map(mapDirectoryEntryToOcLawyer));
        setLastPage(data.data.last_page);
      })
      .catch(() => {
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [officeId, query, page, refresh]);

  const toggleFavorite = async (lawyer: PblOcLawyer) => {
    if (!lawyer.directoryEntryId || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const { data } = await setPblDirectoryFavorite(officeId, lawyer.directoryEntryId, !lawyer.isPersonalFavorite);
      if (data.success !== true) throw new Error('Favorite update failed');
      setLawyers((current) =>
        current.map((entry) =>
          entry.directoryEntryId === lawyer.directoryEntryId
            ? { ...entry, isPreferred: data.data.is_preferred, isPersonalFavorite: data.data.is_personal_favorite }
            : entry
        )
      );
    } catch {
      errorHandler(t('pbl.lawyerDirectory.saveFailed'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <PageContainer title={t('pbl.lawyerDirectory.title')}>
      <DirectoryDescription>{t('pbl.lawyerDirectory.description')}</DirectoryDescription>
      <DirectoryToolbar
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(keyword.trim());
          setPage(1);
        }}
      >
        {offices.length > 1 && (
          <DirectoryField>
            {t('pbl.lawyerDirectory.office')}
            <DirectoryInput
              as="select"
              name="office"
              value={officeId}
              disabled={saving || loading}
              onChange={(event) => {
                setOfficeId(Number(event.target.value));
                setPage(1);
                setPreview(null);
              }}
            >
              {offices.map((office) => (
                <option key={office.pbl_oc_office_id} value={office.pbl_oc_office_id}>
                  {office.oc_office_name}
                </option>
              ))}
            </DirectoryInput>
          </DirectoryField>
        )}
        <DirectoryField>
          {t('pbl.lawyerDirectory.search')}
          <DirectoryInput
            name="keyword"
            autoComplete="off"
            value={keyword}
            disabled={saving}
            onChange={(event) => setKeyword(event.target.value)}
          />
        </DirectoryField>
        <DirectoryButton type="submit" variant="primary" disabled={saving}>
          {t('pbl.lawyerDirectory.search')}
        </DirectoryButton>
      </DirectoryToolbar>
      <DirectoryPanel>
        {loading ? (
          <DirectoryState role="status">{t('pbl.lawyerDirectory.loading')}</DirectoryState>
        ) : failed ? (
          <DirectoryState role="alert">
            {t('pbl.lawyerDirectory.loadFailed')}
            <DirectoryButton type="button" variant="primary-invert" onClick={() => setRefresh((value) => value + 1)}>
              {t('pbl.lawyerDirectory.retry')}
            </DirectoryButton>
          </DirectoryState>
        ) : lawyers.length ? (
          <DirectoryList aria-label={t('pbl.lawyerDirectory.title')}>
            {lawyers.map((lawyer) => (
              <DirectoryRow key={lawyer.directoryEntryId}>
                <DirectoryIdentity>
                  <DirectoryName>{lawyer.name}</DirectoryName>
                  {lawyer.isPreferred && <DirectoryBadge>{t('pbl.lawyerDirectory.officePreferred')}</DirectoryBadge>}
                </DirectoryIdentity>
                <DirectoryActions>
                  <DirectoryButton type="button" variant="primary-invert" onClick={() => setPreview(lawyer)}>
                    {t('pbl.lawyerDirectory.cv')}
                  </DirectoryButton>
                  <DirectoryButton
                    type="button"
                    variant={lawyer.isPersonalFavorite ? 'primary' : 'primary-invert'}
                    disabled={saving}
                    aria-pressed={Boolean(lawyer.isPersonalFavorite)}
                    onClick={() => toggleFavorite(lawyer)}
                  >
                    {t(lawyer.isPersonalFavorite ? 'pbl.lawyerDirectory.remove' : 'pbl.lawyerDirectory.add')}
                  </DirectoryButton>
                </DirectoryActions>
              </DirectoryRow>
            ))}
          </DirectoryList>
        ) : (
          <DirectoryState role="status">{t('pbl.lawyerDirectory.empty')}</DirectoryState>
        )}
      </DirectoryPanel>
      {!loading && !failed && (
        <DirectoryPagination aria-label={t('pbl.lawyerDirectory.pagination')}>
          <DirectoryButton
            type="button"
            variant="primary-invert"
            disabled={page <= 1 || saving}
            onClick={() => setPage((value) => value - 1)}
          >
            {t('pbl.lawyerDirectory.previous')}
          </DirectoryButton>
          <span aria-live="polite">
            {page} / {lastPage}
          </span>
          <DirectoryButton
            type="button"
            variant="primary-invert"
            disabled={page >= lastPage || saving}
            onClick={() => setPage((value) => value + 1)}
          >
            {t('pbl.lawyerDirectory.next')}
          </DirectoryButton>
        </DirectoryPagination>
      )}
      <LawyerReviewSummaryModal
        lawyer={preview}
        language={i18n.language === 'en' ? 'en' : 'zh'}
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
      />
    </PageContainer>
  );
};

export default LawyerDirectory;
